import React, { FormEvent, useState } from 'react'
import { useNotify } from 'react-admin'
import { Box, Grid, Modal, FormControlLabel, Checkbox } from '@mui/material'

import { updateSubscription, SubscriptionStatus, Subscription } from 'api/subscriptions'
import RoundedPanel, { Label } from 'common/RoundedPanel'
import { SumbitButtons } from 'common/SubmitButtons'
import { NormalTextField } from 'common/TextField'
import { CompanyRecord } from 'api/company'

interface UpdateSubscriptionModalProps {
  record: CompanyRecord
  subscription: Subscription
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

const statusOptions = [
  { id: SubscriptionStatus.ACTIVE, name: 'Active' },
  { id: SubscriptionStatus.INACTIVE, name: 'Inactive' },
  { id: SubscriptionStatus.TRIALING, name: 'Trialing' }
]

export const UpdateSubscriptionModal = ({ record, subscription, open, onClose, onSuccess }: UpdateSubscriptionModalProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [makeEndless, setMakeEndless] = useState<boolean>(!subscription?.endDate)
  const notify = useNotify()

  const formatDateForInput = (dateString: string | undefined) => {
    if (!dateString) return ''
    // For ISO datetime format, extract date part
    return dateString.split('T')[0]
  }

  const formatTimeForInput = (dateString: string | undefined) => {
    if (!dateString) return '00:00'
    // For ISO datetime format, extract time part
    const time = dateString.split('T')[1]?.split('.')[0]?.substring(0, 5)
    return time || '00:00'
  }

  const handleSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const formData = new FormData(e.currentTarget)
      const startDate = formData.get('startDate') as string
      const startTime = formData.get('startTime') as string
      const endDate = makeEndless ? undefined : (formData.get('endDate') as string)
      const endTime = makeEndless ? undefined : (formData.get('endTime') as string)
      const status = (formData.get('status') as SubscriptionStatus) || subscription.status

      if (!startDate) {
        notify('Start date is required', { type: 'warning' })
        setIsLoading(false)
        return
      }

      if (!makeEndless && !endDate) {
        notify('End date is required when subscription is not endless', { type: 'warning' })
        setIsLoading(false)
        return
      }

      // Convert dates to ISO string format for API
      const startDateTime = new Date(`${startDate}T${startTime || '00:00'}:00.000Z`).toISOString()
      let endDateTime: string | undefined = undefined
      
      if (!makeEndless && endDate) {
        endDateTime = new Date(`${endDate}T${endTime || '23:59'}:00.000Z`).toISOString()
        const endDateObj = new Date(endDateTime)
        const startDateObj = new Date(startDateTime)
        const now = new Date()
        
        if (endDateObj < now) {
          notify('End date cannot be in the past', { type: 'warning' })
          setIsLoading(false)
          return
        }
        
        if (startDateObj > endDateObj) {
          notify('Start date cannot be after end date', { type: 'warning' })
          setIsLoading(false)
          return
        }
      }

      await updateSubscription(subscription.companyId, {
        startDate: startDateTime,
        endDate: endDateTime,
        status,
        makeEndless
      })

      notify('Subscription updated successfully', { type: 'success' })
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Error updating subscription:', error)
      notify('Failed to update subscription', { type: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableAutoFocus={true}
    >
      <RoundedPanel modal>
        <h1>Update Subscription</h1>
        <Box height={32} />
        <Label noSelect margin>Company: </Label>{record.name}
        <Box height={16} />
        <Label noSelect margin>Subscription Version: </Label>{subscription.version}
        <Box height={32} />

        <form onSubmit={handleSave}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <NormalTextField
                name="startDate"
                label="Start Date"
                type="date"
                fullWidth
                required
                defaultValue={formatDateForInput(subscription?.startDate)}
                InputLabelProps={{
                  shrink: true
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <NormalTextField
                name="startTime"
                label="Start Time"
                type="time"
                fullWidth
                defaultValue={formatTimeForInput(subscription?.startDate)}
                InputLabelProps={{
                  shrink: true
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={makeEndless}
                    onChange={(e) => setMakeEndless(e.target.checked)}
                  />
                }
                label="Make subscription endless"
              />
            </Grid>

            {!makeEndless && (
              <>
                <Grid item xs={6}>
                  <NormalTextField
                    name="endDate"
                    label="End Date"
                    type="date"
                    fullWidth
                    required={!makeEndless}
                    defaultValue={subscription?.endDate ? formatDateForInput(subscription.endDate) : ''}
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <NormalTextField
                    name="endTime"
                    label="End Time"
                    type="time"
                    fullWidth
                    defaultValue={subscription?.endDate ? formatTimeForInput(subscription.endDate) : '23:59'}
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <NormalTextField
                select
                name="status"
                label="Status"
                fullWidth
                defaultValue={subscription?.status || SubscriptionStatus.ACTIVE}
                SelectProps={{
                  native: true
                }}
              >
                {statusOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </NormalTextField>
            </Grid>
          </Grid>

          <SumbitButtons 
            onClose={onClose} 
            isLoading={isLoading}
            onSaveLabel="Update"
          />
        </form>
      </RoundedPanel>
    </Modal>
  )
} 